// v3.93 - Dark mode support: theme-aware bgcolor + transparent logos replacing text placeholder - 2026-03-19
// v2.55 - Forgot Password page with Clerk - 2026-02-04
import React, { useState } from 'react';
import MyLojicLogo from '../components/MyLojicLogo';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { useTheme } from '@mui/material/styles'; // v3.93
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import { colors } from '../theme/colors';

type ForgotPasswordPhase = 'request' | 'reset' | 'success';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const muiTheme = useTheme(); // v3.93
  const isDark = muiTheme.palette.mode === 'dark';
  const { t } = useTranslation('auth');
  const { signIn, setActive, isLoaded } = useSignIn();

  // State
  const [phase, setPhase] = useState<ForgotPasswordPhase>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Request password reset
  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    setIsLoading(true);
    setError(null);

    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });
      setPhase('reset');
    } catch (err: any) {
      console.error('Password reset request error:', err);
      const errorCode = err.errors?.[0]?.code;
      if (errorCode === 'form_identifier_not_found') {
        setError(t('forgotPassword.errors.emailNotFound'));
      } else if (errorCode === 'too_many_attempts') {
        setError(t('forgotPassword.errors.tooManyAttempts'));
      } else {
        setError(t('forgotPassword.errors.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password with code
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError(t('resetPassword.errors.passwordMismatch'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password: newPassword,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        setPhase('success');
      }
    } catch (err: any) {
      console.error('Password reset error:', err);
      const errorCode = err.errors?.[0]?.code;
      if (errorCode === 'form_code_incorrect') {
        setError(t('verification.errors.invalidCode'));
      } else if (errorCode === 'form_password_pwned') {
        setError(t('resetPassword.errors.weakPassword'));
      } else {
        setError(t('resetPassword.errors.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while Clerk initializes
  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress sx={{ color: colors.primary.main }} />
      </Box>
    );
  }

  // Success phase - password reset complete
  if (phase === 'success') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', py: 4 }}>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: { xs: 3, sm: 6 }, borderRadius: 2, textAlign: 'center' }}>
            {/* v3.93 - real logo replacing text placeholder */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <MyLojicLogo isDark={isDark} height={48} />
            </Box>
            <Alert severity="success" sx={{ mb: 3, bgcolor: colors.status.successLight }}>
              {t('resetPassword.success')}
            </Alert>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => navigate('/dashboard')}
              sx={{
                bgcolor: colors.primary.main,
                color: colors.primary.contrastText,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { bgcolor: colors.primary.dark },
              }}
            >
              Go to Dashboard
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 6 }, borderRadius: 2 }}>
          {/* Logo */}
          <Typography
            variant="h3"
            sx={{
              color: colors.secondary.main,
              fontWeight: 700,
              textAlign: 'center',
              mb: 1,
            }}
          >
            mylojic.com
          </Typography>

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              color: colors.text.primary,
              fontWeight: 600,
              textAlign: 'center',
              mb: 0.5,
            }}
          >
            {phase === 'request' ? t('forgotPassword.title') : t('resetPassword.title')}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body1"
            sx={{
              color: colors.text.secondary,
              textAlign: 'center',
              mb: 4,
            }}
          >
            {phase === 'request' ? t('forgotPassword.subtitle') : t('resetPassword.subtitle')}
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, bgcolor: colors.status.errorLight }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {phase === 'request' ? (
            /* Request Reset Form */
            <Box component="form" onSubmit={handleRequestReset}>
              <TextField
                fullWidth
                label={t('forgotPassword.emailLabel')}
                placeholder={t('forgotPassword.emailPlaceholder')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
                autoComplete="email"
                autoFocus
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading || !email}
                sx={{
                  bgcolor: colors.primary.main,
                  color: colors.primary.contrastText,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { bgcolor: colors.primary.dark },
                  '&:disabled': { bgcolor: colors.border.main },
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: 'inherit', mr: 1 }} />
                    {t('forgotPassword.sending')}
                  </>
                ) : (
                  t('forgotPassword.submitButton')
                )}
              </Button>
            </Box>
          ) : (
            /* Reset Password Form */
            <Box component="form" onSubmit={handleResetPassword}>
              <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 2 }}>
                We sent a verification code to <strong>{email}</strong>
              </Typography>

              <TextField
                fullWidth
                label={t('verification.codeLabel')}
                placeholder={t('verification.codePlaceholder')}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={isLoading}
                required
                autoFocus
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 6 }}
              />

              <TextField
                fullWidth
                label={t('resetPassword.passwordLabel')}
                placeholder={t('resetPassword.passwordPlaceholder')}
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                required
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={t('signup.passwordRequirements')}
              />

              <TextField
                fullWidth
                label={t('resetPassword.confirmPasswordLabel')}
                placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                required
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading || !code || !newPassword || !confirmPassword}
                sx={{
                  bgcolor: colors.primary.main,
                  color: colors.primary.contrastText,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { bgcolor: colors.primary.dark },
                  '&:disabled': { bgcolor: colors.border.main },
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: 'inherit', mr: 1 }} />
                    {t('resetPassword.resetting')}
                  </>
                ) : (
                  t('resetPassword.submitButton')
                )}
              </Button>
            </Box>
          )}

          {/* Back to Login Link */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                color: colors.primary.main,
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <ArrowBack sx={{ fontSize: 18, mr: 0.5 }} />
              {t('forgotPassword.backToLogin')}
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
